import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const KEY = "willow.basket.v1";
const BasketContext = createContext(null);

/** A tiny meal-plan basket persisted in localStorage. Items are lightweight
 *  snapshots of a Recipes idea so the /basket page can render without a fetch. */
export function BasketProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });

  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify(items)); } catch {}
  }, [items]);

  const add = useCallback((idea) => {
    if (!idea || idea.category !== "Recipes") return;
    setItems((prev) => {
      if (prev.some((i) => i.id === idea.id)) return prev;
      const snap = {
        id: idea.id,
        title: idea.title,
        author_name: idea.author_name,
        photo_path: idea.photo_path,
        servings: idea.servings || null,
        total_time: idea.total_time || null,
        context: idea.context,
        added_at: new Date().toISOString(),
      };
      toast(`Added "${idea.title}" to your basket.`);
      return [snap, ...prev];
    });
  }, []);

  const remove = useCallback((id) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const clear = useCallback(() => setItems([]), []);
  const has = useCallback((id) => items.some((i) => i.id === id), [items]);

  const reorder = useCallback((fromIndex, toIndex) => {
    setItems((prev) => {
      if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= prev.length || toIndex >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }, []);

  const value = useMemo(() => ({ items, add, remove, clear, has, reorder, count: items.length }), [items, add, remove, clear, has, reorder]);
  return <BasketContext.Provider value={value}>{children}</BasketContext.Provider>;
}

export const useBasket = () => useContext(BasketContext);
